import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-react';
import { setupScreenShareEventListeners } from '../events/setupEventListeners';
import { DISPLAY_SURFACE_TYPES } from '@studio/constants/DisplaySurfaceTypes';

export type ScreenShareTrackingProps = {
  screenShareType: string;
  tabAudioShared: boolean;
};

/**
 * Derives the screen share type and audio sharing status from Agora tracks.
 * @param screenVideoTrack - The screen video track to extract display surface info from.
 * @param screenAudioTrack - The optional screen audio track.
 * @returns An object with screenShareType and tabAudioShared.
 */
const getScreenShareTrackingProps = (
  screenVideoTrack: ILocalVideoTrack,
  screenAudioTrack?: ILocalAudioTrack
): ScreenShareTrackingProps => {
  const settings = screenVideoTrack.getMediaStreamTrack()?.getSettings() as MediaTrackSettings & {
    displaySurface?: string;
  };
  const displaySurface = settings?.displaySurface;
  const screenShareType =
    (displaySurface && DISPLAY_SURFACE_TYPES[displaySurface as keyof typeof DISPLAY_SURFACE_TYPES]) ||
    displaySurface ||
    'unknown';
  const tabAudioShared = !!screenAudioTrack;
  return { screenShareType, tabAudioShared };
};

/**
 * Starts screen share for an Agora attendee by creating and publishing the screen video track
 * @param channel - The channel ID to join for screen sharing (Broadcast ID).
 * @param token - The token for authentication.
 * @param appId - The Agora App ID from the environment variables.
 * @param screenShareAttendeeId - The ID of the attendee starting the screen share.
 * @param screenShareClient - (Optional) An existing Agora RTC client instance. If not provided, a new client will be created.
 * @param onScreenShareEnded - (Optional) Callback function that will be called after Agora cleanup when browser stops screen sharing.
 * @returns An object containing the Agora RTC client, screen video track, optional screen audio track, and tracking props.
 */
export const startScreenShare = async (
  channel: string,
  token: string,
  appId: string,
  screenShareAttendeeId: string,
  screenShareClient?: IAgoraRTCClient,
  onScreenShareEnded?: () => void
): Promise<{
  client: IAgoraRTCClient;
  screenVideoTrack: ILocalVideoTrack;
  screenAudioTrack?: ILocalAudioTrack;
  screenShareTrackingProps: ScreenShareTrackingProps;
}> => {
  const client = screenShareClient ?? AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
  await client.join(appId, channel, token, screenShareAttendeeId);

  let screenVideoTrack: ILocalVideoTrack | undefined;
  let screenAudioTrack: ILocalAudioTrack | undefined;

  try {
    const screenTrack = await AgoraRTC.createScreenVideoTrack({}, 'auto');

    if (Array.isArray(screenTrack)) {
      [screenVideoTrack, screenAudioTrack] = screenTrack;
    } else {
      screenVideoTrack = screenTrack;
    }

    const tracks = screenAudioTrack ? [screenVideoTrack, screenAudioTrack] : [screenVideoTrack];

    await client.publish(tracks);

    // Set up event listener for when browser stops screen sharing
    if (onScreenShareEnded) {
      setupScreenShareEventListeners(screenVideoTrack, client, screenAudioTrack, onScreenShareEnded);
    }

    const screenShareTrackingProps = getScreenShareTrackingProps(screenVideoTrack, screenAudioTrack);

    return {
      client,
      screenVideoTrack,
      screenAudioTrack,
      screenShareTrackingProps
    };
  } catch (error) {
    // Cleanup for when user cancels the screen share browser prompt
    if (screenVideoTrack) {
      screenVideoTrack.stop();
      screenVideoTrack.close();
    }
    if (screenAudioTrack) {
      screenAudioTrack.stop();
      screenAudioTrack.close();
    }

    await client.leave();
    throw error;
  }
};

/**
 * Stops screen share for an Agora attendee by unpublishing the tracks
 *
 * @param screenShareClient - The Agora RTC client instance.
 * @param screenVideoTrack - The screen video track to be stopped.
 * @param screenAudioTrack - (Optional) The screen audio track to be stopped.
 */
export async function stopScreenShare(
  screenShareClient: IAgoraRTCClient,
  screenVideoTrack: ILocalVideoTrack,
  screenAudioTrack?: ILocalAudioTrack
): Promise<void> {
  const tracks = screenAudioTrack ? [screenVideoTrack, screenAudioTrack] : [screenVideoTrack];

  if (screenVideoTrack) {
    screenVideoTrack.stop();
    screenVideoTrack.close();
  }

  if (screenAudioTrack) {
    screenAudioTrack.stop();
    screenAudioTrack.close();
  }

  // Only unpublish and leave if client exists
  if (screenShareClient) {
    await screenShareClient.unpublish(tracks);
    await screenShareClient.leave();
  }
}
